import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  AccessTime as AccessTimeIcon,
  ArrowBack as ArrowBackIcon,
  AssignmentTurnedIn as AssignmentTurnedInIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  CloudUpload as CloudUploadIcon,
  Download as DownloadIcon,
  Save as SaveIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  LinearProgress,
  Fade,
  Modal,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from "@mui/material";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import HeaderTitle from "../../../components/common/HeaderTitle/HeaderTitle";
import SearchBar from "../../../components/common/SearchBar/SearchBar";
import PaginationComponent from "../../../components/common/Pagination/PaginationComponent";
import SuccessPopupDashboard from "../../../components/Popups/PopupFrame/SuccessPopupDashboard";
import successPopupImg from "../../../assets/images/png/Group 2 2@2x.png";
import {
  compareServiceDocuments,
  getServiceMatchingDetails,
  getServiceMatchingList,
  getSortableAmount,
  saveServiceMatchingResult,
  toServiceMatchingListRecord,
} from "./serviceMatchingService";

const PAGE_SIZE = 10;
const PDF_EXPORT_WIDTH = 1280;
const PDF_EXPORT_MIN_HEIGHT = 900;
const FONT_REGULAR = "GilroyRegular";
const FONT_BOLD = "GilroyBold";
const MIN_FONT_SIZE = "14px";
const TABLE_TEXT_COLOR = "#000000";

const textSx = {
  fontFamily: FONT_REGULAR,
  fontSize: MIN_FONT_SIZE,
  color: TABLE_TEXT_COLOR,
};

const mutedTextSx = {
  fontFamily: FONT_REGULAR,
  fontSize: MIN_FONT_SIZE,
  color: "#64748b",
};

const tableHeadRowSx = {
  background: "linear-gradient(180deg, #f8fbff 0%, #eef6ff 100%)",
  "& th": {
    py: 1.2,
    borderBottom: "1px solid #bfdbfe",
    fontWeight: 700,
    fontFamily: FONT_BOLD,
    fontSize: MIN_FONT_SIZE,
    color: TABLE_TEXT_COLOR,
  },
};

const tableBodyRowSx = {
  "&:nth-of-type(even)": {
    backgroundColor: "#fbfdff",
  },
  "&:hover": {
    backgroundColor: "#eef6ff !important",
    boxShadow: "inset 3px 0 0 #1976d2",
  },
  "& td": {
    py: 1,
    fontFamily: FONT_REGULAR,
    fontSize: MIN_FONT_SIZE,
    color: `${TABLE_TEXT_COLOR} !important`,
    borderBottom: "1px solid #e2e8f0",
    verticalAlign: "top",
    "& .MuiTypography-root": {
      color: `${TABLE_TEXT_COLOR} !important`,
    },
  },
};

const tablePaperSx = {
  fontFamily: FONT_REGULAR,
  borderRadius: "10px",
  border: "1px solid #dbeafe",
  boxShadow: "0 10px 26px rgba(15, 23, 42, 0.08)",
  overflow: "hidden",
};

const tableContainerSx = {
  overflow: "auto",
  WebkitOverflowScrolling: "touch",
  "&::-webkit-scrollbar": { height: 0, width: 0 },
};

const actionButtonSx = {
  textTransform: "none",
  borderRadius: 2,
  fontFamily: FONT_BOLD,
  fontSize: MIN_FONT_SIZE,
};

const tooltipProps = {
  componentsProps: {
    tooltip: {
      sx: {
        fontFamily: FONT_REGULAR,
        fontSize: "13px",
      },
    },
  },
  slotProps: {
    tooltip: {
      sx: {
        fontFamily: FONT_REGULAR,
        fontSize: "13px",
      },
    },
  },
};

const listColumns = [
  { key: "serial", label: "S.No", width: 60 },
  { key: "serviceMatchId", label: "Service Match ID", width: 145 },
  { key: "matchingMode", label: "Match Type", width: 90 },
  { key: "sowNo", label: "SOW No", width: 95 },
  { key: "vendor", label: "Vendor", width: 130, noWrap: true },
  { key: "servicePeriod", label: "Service Period", width: 125 },
  { key: "invoiceNumber", label: "Invoice No", width: 115 },
  { key: "resources", label: "Resources", width: 75, align: "right" },
  { key: "approvedHours", label: "Approved Hours", width: 125, align: "right" },
  { key: "expectedBilling", label: "Expected Billing", width: 140, align: "right" },
  { key: "invoiceAmount", label: "Invoice Amount", width: 135, align: "right" },
  { key: "varianceAmount", label: "Variance", width: 115, align: "right" },
  { key: "currency", label: "Currency", width: 85 },
  // { key: "inrEquivalent", label: "INR Equivalent", width: 145, align: "right" },
  { key: "overallStatus", label: "Overall Status", width: 145 },
  { key: "createdDate", label: "Created Date", width: 150, sortable: true },
];

const detailColumns = [
  { key: "serial", label: "S.No", width: 64 },
  { key: "consultantName", label: "Consultant Name", width: 180 },
  { key: "role", label: "Role", width: 170 },
  { key: "currency", label: "Currency", width: 100 },
  { key: "hourlyRate", label: "SOW Hourly Rate", width: 150, align: "right" },
  { key: "fixedFee", label: "SOW Fixed Fee", width: 140, align: "right" },
  { key: "approvedHours", label: "Timesheet Approved Hours", width: 240, align: "right" },
  { key: "billingPercentage", label: "SOW Billing %", width: 140, align: "right"},
  { key: "billableHours", label: "Billable Hours", width: 140, align: "right" },
  { key: "expectedAmount", label: "Expected Amount", width: 170, align: "right" },
  { key: "exchangeRate", label: "Exchange Rate", width: 140, align: "right" },
  { key: "invoiceHours", label: "Invoice Hours", width: 130, align: "right" },
  { key: "invoiceHourlyRate", label: "Invoice Rate", width: 140, align: "right" },
  { key: "invoiceAmount", label: "Invoice Amount", width: 160, align: "right" },
  { key: "invoiceVariance", label: "Invoice Variance", width: 160, align: "right" },
  { key: "matchStatus", label: "Match Status", width: 180 },
  { key: "contractStatus", label: "SOW Contract Status", width: 190 },
  { key: "approvalStatus", label: "Timesheet Approval Status", width: 210 },
  { key: "periodStatus", label: "Timesheet Period Status", width: 200 },
  { key: "billingStatus", label: "Billing Status", width: 170 },
  { key: "invoiceStatus", label: "Invoice Status", width: 180 },
  { key: "overallStatus", label: "Overall Status", width: 170 },
  { key: "remarks", label: "Remarks", width: 240 },
];

const listTableMinWidth = listColumns.reduce((sum, column) => sum + column.width, 130);
const detailTableMinWidth = detailColumns.reduce((sum, column) => sum + column.width, 0);
const detailStickyLeft = {
  serial: 0,
  consultantName: detailColumns.find((column) => column.key === "serial")?.width || 64,
};
const detailStickyRight = {
  remarks: 0,
  overallStatus: detailColumns.find((column) => column.key === "remarks")?.width || 240,
};
const getDetailColumn = (key) => detailColumns.find((column) => column.key === key) || { key, width: 140 };

const detailColumnTooltips = {
  contractStatus: "SOW / contract validation status",
  approvalStatus: "Timesheet approval validation status",
  periodStatus: "Timesheet service period validation status",
  invoiceStatus: "Invoice line validation status",
  matchStatus: "Combined SOW, timesheet, and invoice matching status",
  billingStatus: "Billing rule validation status",
  overallStatus: "Overall consultant matching status",
};

const isEmptyValue = (value) =>
  value === null || value === undefined || value === "";

const formatNumber = (value, decimals = 2) => {
  if (isEmptyValue(value)) return "-";
  if (typeof value === "string" && /[^0-9.-]/.test(value)) return value;

  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return String(value);

  return parsed.toLocaleString("en-IN", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

const formatAmount = (value, currency) =>
  typeof value === "string" && /[^0-9.-]/.test(value)
    ? value
    : `${currency || ""} ${formatNumber(value)}`.trim();

const formatInrAmount = (value) =>
  isEmptyValue(value)
    ? "-"
    : typeof value === "string" && /[^0-9.-]/.test(value)
    ? value
    : `INR ${formatNumber(value)}`;

const getRecordSearchText = (record = {}) =>
  [
    record.serviceMatchId,
    record.sowNo,
    record.vendor,
    record.servicePeriod,
    record.timesheetRef,
    record.overallStatus,
  ]
    .join(" ")
    .toLowerCase();

// Complexity: filter O(n * q), sort O(n log n), clone space O(n).
const getFilteredAndSortedRecords = (records, searchTerm, sortConfig) => {
  const query = searchTerm.trim().toLowerCase();
  const filtered = query
    ? records.filter((record) => getRecordSearchText(record).includes(query))
    : records;

  return [...filtered].sort((a, b) => {
    const aValue = a[sortConfig.key];
    const bValue = b[sortConfig.key];
    if (["expectedBilling", "invoiceAmount", "varianceAmount", "inrEquivalent"].includes(sortConfig.key)) {
      return sortConfig.direction === "desc"
        ? getSortableAmount(bValue) - getSortableAmount(aValue)
        : getSortableAmount(aValue) - getSortableAmount(bValue);
    }
    if (typeof aValue === "number" && typeof bValue === "number") {
      return sortConfig.direction === "desc" ? bValue - aValue : aValue - bValue;
    }
    return sortConfig.direction === "desc"
      ? String(bValue || "").localeCompare(String(aValue || ""))
      : String(aValue || "").localeCompare(String(bValue || ""));
  });
};

// Complexity: O(k) time and space for the visible page, where k is page size.
const getPagedRecords = (records, currentPage, pageSize) =>
  records.slice((currentPage - 1) * pageSize, currentPage * pageSize);

const PriceValue = ({ value, currency }) => (
  <Typography sx={textSx}>
    {formatAmount(value, currency)}
  </Typography>
);

const formatDate = (date) => {
  if (!date) return "-";
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return date;
  return parsed.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getExchangeRateText = (exchangeRate = {}) =>
  `${exchangeRate?.sourceCurrency || "-"}/${exchangeRate?.targetCurrency || "-"} ${formatNumber(exchangeRate?.exchangeRate, 4)}`;

const getMainExportCellValue = (record = {}, key, serial) => {
  const valueMap = {
    serial,
    serviceMatchId: record.serviceMatchId,
    matchingMode: record.matchingMode,
    sowNo: record.sowNo,
    vendor: record.vendor,
    servicePeriod: record.servicePeriod,
    invoiceNumber: record.invoiceNumber || "-",
    resources: record.resources,
    approvedHours: formatNumber(record.approvedHours, 0),
    expectedBilling: formatAmount(record.expectedBilling, record.currency),
    invoiceAmount: formatAmount(record.invoiceAmount, record.currency),
    varianceAmount: formatAmount(record.varianceAmount, record.currency),
    currency: record.currency,
    // inrEquivalent: `${formatInrAmount(record.inrEquivalent)}\n${getExchangeRateText(record.exchangeRate)}`,
    overallStatus: record.overallStatus,
    createdDate: formatDate(record.createdDate),
  };

  return valueMap[key] ?? "-";
};

const getDetailExportCellValue = (row = {}, key, serial) => {
  const valueMap = {
    serial,
    consultantName: row.consultantName,
    role: row.role,
    currency: row.currency,
    hourlyRate: formatAmount(row.hourlyRate, row.currency),
    fixedFee: formatAmount(row.fixedFee, row.currency),
    approvedHours: formatNumber(row.approvedHours, 0),
    billingPercentage: `${formatNumber(row.billingPercentage, 0)}%`,
    billableHours: formatNumber(row.billableHours, 2),
    expectedAmount: formatAmount(row.expectedAmount, row.currency),
    exchangeRate: `${formatNumber(row.exchangeRate, 4)}\n${formatDate(row.exchangeRateDate)}`,
    invoiceHours: formatNumber(row.invoiceHours, 2),
    invoiceHourlyRate: formatAmount(row.invoiceHourlyRate, row.currency),
    invoiceAmount: formatAmount(row.invoiceAmount, row.currency),
    invoiceVariance: formatAmount(row.invoiceVariance, row.currency),
    matchStatus: row.matchStatus,
    contractStatus: row.contractStatus,
    approvalStatus: row.approvalStatus,
    periodStatus: row.periodStatus,
    billingStatus: row.billingStatus,
    invoiceStatus: row.invoiceStatus,
    overallStatus: row.overallStatus,
    remarks: row.remarks,
  };

  return valueMap[key] ?? "-";
};

const getSummaryExportRows = (details = {}) => [
  { Field: "Overall Status", Value: details.summary?.overallStatus || "-" },
  { Field: "Matching Mode", Value: details.matchingMode || "-" },
  { Field: "Matched / Unmatched", Value: `${formatNumber(details.summary?.matchedCount, 0)} / ${formatNumber(details.summary?.unmatchedCount, 0)}` },
  { Field: "Contract Coverage", Value: details.summary?.contractCoverage || "-" },
  { Field: "Timesheet Approval", Value: details.summary?.timesheetApproval || "-" },
  { Field: "Special Billing Conditions", Value: details.summary?.specialBillingConditions || "-" },
  { Field: "Period Issues", Value: details.summary?.periodIssues || "-" },
  { Field: "Original Expected Billing", Value: formatAmount(details.summary?.originalExpectedBilling, details.currency) },
  { Field: "Invoice Amount", Value: formatAmount(details.summary?.invoiceAmount, details.currency) },
  { Field: "Invoice Variance", Value: formatAmount(details.summary?.invoiceVariance, details.currency) },
  { Field: "Exchange Rate + Date", Value: `${getExchangeRateText(details.exchangeRate)} | ${formatDate(details.exchangeRate?.exchangeRateDate)}` },
  { Field: "Expected Billing in INR", Value: formatInrAmount(details.summary?.expectedBillingInInr) },
];

const waitForPaint = () =>
  new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(resolve));
  });

const saveElementAsPdf = async (element, fileName) => {
  if (!element) return;
  try {
    if (document.fonts?.ready) await document.fonts.ready;
  } catch (_) {}
  await waitForPaint();

  const rootWidth = Math.max(PDF_EXPORT_WIDTH, Math.ceil(element.scrollWidth || element.offsetWidth || 0));
  const rootHeight = Math.max(1, Math.ceil(element.scrollHeight || element.offsetHeight || 0));
  const canvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    allowTaint: false,
    backgroundColor: "#ffffff",
    logging: false,
    scrollX: 0,
    scrollY: 0,
    width: rootWidth,
    height: rootHeight,
    windowWidth: Math.max(rootWidth, PDF_EXPORT_WIDTH),
    windowHeight: Math.max(rootHeight, PDF_EXPORT_MIN_HEIGHT),
    onclone: (clonedDoc) => {
      const clonedRoot = clonedDoc.querySelector('[data-service-matching-export-root="true"]');
      if (clonedRoot) {
        clonedRoot.style.width = `${PDF_EXPORT_WIDTH}px`;
        clonedRoot.style.maxWidth = "none";
        clonedRoot.style.overflow = "visible";
      }
      clonedDoc.documentElement.style.width = `${PDF_EXPORT_WIDTH}px`;
      clonedDoc.body.style.width = `${PDF_EXPORT_WIDTH}px`;
    },
  });

  const pdf = new jsPDF("p", "mm", "a4");
  const margin = 8;
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const usableWidth = pageWidth - margin * 2;
  const usableHeight = pageHeight - margin * 2;
  const pageHeightPx = Math.max(1, Math.floor((usableHeight * canvas.width) / usableWidth));
  const sliceCanvas = document.createElement("canvas");
  const sliceCtx = sliceCanvas.getContext("2d");
  let sourceY = 0;
  let hasRendered = false;

  while (sliceCtx && sourceY < canvas.height) {
    const sliceHeight = Math.min(pageHeightPx, canvas.height - sourceY);
    sliceCanvas.width = canvas.width;
    sliceCanvas.height = sliceHeight;
    sliceCtx.fillStyle = "#ffffff";
    sliceCtx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
    sliceCtx.drawImage(canvas, 0, sourceY, canvas.width, sliceHeight, 0, 0, canvas.width, sliceHeight);

    const imageData = sliceCanvas.toDataURL("image/jpeg", 0.95);
    const imageHeight = (sliceHeight * usableWidth) / canvas.width;
    if (hasRendered) pdf.addPage();
    pdf.addImage(imageData, "JPEG", margin, margin, usableWidth, imageHeight);
    hasRendered = true;
    sourceY += sliceHeight;
  }

  if (hasRendered) pdf.save(fileName);
};

const getStatusType = (status = "") => {
  const normalized = String(status).toLowerCase();
  if (normalized.includes("contract rate available")) return "contract";
  if (normalized.includes("variance")) return "review";
  if (normalized.includes("invoice line not found")) return "missingDependency";
  if (normalized.includes("unmatched")) return "rejected";
  if (normalized === "approved") return "approved";
  if (normalized.includes("conditional")) return "conditional";
  if (normalized.includes("matched") && !normalized.includes("not") && !normalized.includes("partial")) return "matched";
  if (normalized.includes("partial")) return "partial";
  if (normalized.includes("review")) return "review";
  if (normalized.includes("pending")) return "pending";
  if (normalized.includes("rejected") || normalized.includes("not matched")) return "rejected";
  if (normalized.includes("no timesheet") || normalized.includes("not found")) return "missingDependency";
  return "notCompared";
};

const StatusChip = ({ status }) => {
  const type = getStatusType(status);
  const config = {
    approved: { color: "#1B5E20", bgcolor: "#E8F5E9", border: "#81C784" },
    contract: { color: "#0F4C81", bgcolor: "#E8F1FA", border: "#90CAF9" },
    conditional: { color: "#5D4037", bgcolor: "#EFEBE9", border: "#BCAAA4" },
    missingDependency: { color: "#7C2D12", bgcolor: "#FFF7ED", border: "#FDBA74" },
    notCompared: { color: "#334155", bgcolor: "#F8FAFC", border: "#E2E8F0" },
    matched: { color: "#2E7D32", bgcolor: "#E8F5E9", border: "#A5D6A7" },
    partial: { color: "#ED6C02", bgcolor: "#FFF3E0", border: "#FFB74D" },
    pending: { color: "#7A4F01", bgcolor: "#FFF8E1", border: "#FFD54F" },
    review: { color: "#6A1B9A", bgcolor: "#F3E5F5", border: "#CE93D8" },
    rejected: { color: "#D32F2F", bgcolor: "#FFEBEE", border: "#EF9A9A" },
  };
  const { color, bgcolor, border } = config[type] || config.notCompared;
  const icon =
    type === "matched" ? (
      <CheckCircleIcon sx={{ fontSize: 16, color }} />
    ) : type === "rejected" ? (
      <CancelIcon sx={{ fontSize: 16, color }} />
    ) : (
      <AccessTimeIcon sx={{ fontSize: 16, color }} />
    );

  const label = status || "-";

  return (
    <Tooltip
      title={<Typography sx={{ fontFamily: FONT_REGULAR, fontSize: "13px" }}>{label}</Typography>}
      arrow
      enterDelay={250}
      leaveDelay={100}
      {...tooltipProps}
    >
      <Chip
        icon={icon}
        label={label}
        size="small"
        sx={{
          fontFamily: FONT_REGULAR,
          fontSize: MIN_FONT_SIZE,
          height: "28px",
          maxWidth: "100%",
          backgroundColor: bgcolor,
          color,
          border: `1px solid ${border}`,
          cursor: "default",
          "& .MuiChip-icon": { ml: "8px", mr: "-4px" },
          "& .MuiChip-label": {
            px: 0.9,
            overflow: "hidden",
            textOverflow: "ellipsis",
          },
        }}
      />
    </Tooltip>
  );
};

const EmptyState = ({ title = "No records found", subtitle }) => (
  <Box sx={{ textAlign: "center", py: 8, px: 2, color: "text.secondary" }}>
    <AssignmentTurnedInIcon sx={{ fontSize: 48, opacity: 0.5, mb: 1 }} />
    <Typography variant="h6" sx={{ fontWeight: 600, fontFamily: FONT_BOLD, fontSize: "18px" }}>
      {title}
    </Typography>
    {subtitle && (
      <Typography variant="body2" sx={{ ...mutedTextSx, mt: 0.5 }}>
        {subtitle}
      </Typography>
    )}
  </Box>
);

const MultiFileUploadBox = ({ files, onChange, onClear }) => (
  <Box>
    <Typography sx={{ mb: 1, fontFamily: FONT_BOLD, fontSize: MIN_FONT_SIZE, color: "#334155" }}>
      SOW / Contract, Timesheet / Approval and Invoice
    </Typography>
    <Box
      component="label"
      sx={{
        width: "100%",
        minHeight: 144,
        display: "flex",
        alignItems: "center",
        gap: 2,
        border: "1px dashed #94a3b8",
        borderRadius: 1,
        px: { xs: 2, sm: 3 },
        py: 2,
        cursor: "pointer",
        background: "linear-gradient(135deg, #ffffff 0%, #f8fbff 100%)",
        transition: "all 0.2s ease",
        "&:hover": {
          borderColor: "#1976d2",
          boxShadow: "0 8px 22px rgba(25, 118, 210, 0.12)",
        },
      }}
    >
      <Box sx={{ minWidth: 0, flex: 1 }}>
        {files.length ? (
          <Stack direction="row" flexWrap="wrap" gap={1}>
            {files.map((file) => (
              <Chip
                key={`${file.name}-${file.size}`}
                label={file.name}
                size="small"
                sx={{
                  maxWidth: "100%",
                  height: 28,
                  borderRadius: "6px",
                  fontFamily: FONT_REGULAR,
                  fontSize: MIN_FONT_SIZE,
                  color: "#0f4c81",
                  backgroundColor: "#e8f1fa",
                  border: "1px solid #bfdbfe",
                  "& .MuiChip-label": {
                    display: "block",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                }}
              />
            ))}
          </Stack>
        ) : (
          <Typography sx={{ ...textSx, wordBreak: "break-word" }}>
            No files selected
          </Typography>
        )}
        <Typography sx={{ ...mutedTextSx, mt: 0.5 }}>
          Select SOW, timesheet approval and invoice files for 2-way or 3-way matching
        </Typography>
      </Box>
      <Stack direction={{ xs: "column", sm: "row" }} gap={1}>
        {files.length > 0 && (
          <Button
            variant="outlined"
            color="error"
            startIcon={<CancelIcon />}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onClear();
            }}
            sx={actionButtonSx}
          >
            Clear
          </Button>
        )}
        <Button variant="outlined" component="span" sx={actionButtonSx}>
          Browse
        </Button>
      </Stack>
      <input
        type="file"
        multiple
        hidden
        onChange={(event) => {
          onChange(Array.from(event.target.files || []));
          event.target.value = "";
        }}
      />
    </Box>
  </Box>
);

const SummaryCard = ({ label, value }) => (
  <Paper
    elevation={0}
    sx={{
      border: "1px solid #e2e8f0",
      borderRadius: 2,
      p: 1.5,
      height: "100%",
      backgroundColor: "#ffffff",
    }}
  >
    <Typography sx={mutedTextSx}>
      {label}
    </Typography>
    {React.isValidElement(value) ? (
      <Box sx={{ mt: 0.75 }}>{value}</Box>
    ) : (
      <Typography sx={{ mt: 0.5, fontFamily: FONT_BOLD, fontSize: MIN_FONT_SIZE, color: "#0f172a" }}>
        {value}
      </Typography>
    )}
  </Paper>
);

const ProcessStatusPanel = ({ progress }) => {
  if (!progress) return null;

  const normalizedStatus = String(progress.status || "").replace(/_/g, " ");
  const processedLabel = progress.total_files
    ? `${progress.processed_files || 0}/${progress.total_files} files`
    : "";

  return (
    <Paper
      elevation={0}
      sx={{
        mt: 2,
        border: "1px solid #bfdbfe",
        borderRadius: 2,
        p: 2,
        backgroundColor: "#eff6ff",
      }}
    >
      <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "flex-start", sm: "center" }} justifyContent="space-between" gap={1.5}>
        <Stack direction="row" alignItems="center" gap={1}>
          <CircularProgress size={18} />
          <Typography sx={{ fontFamily: FONT_BOLD, fontSize: MIN_FONT_SIZE, color: "#1e3a8a" }}>
            {progress.stage === "fetching" ? "Fetching matching result" : progress.stage === "uploading" ? "Uploading documents" : "Extracting and matching documents"}
          </Typography>
        </Stack>
        <StatusChip status={normalizedStatus || "Processing"} />
      </Stack>
      {processedLabel && (
        <Typography sx={{ ...mutedTextSx, mt: 1 }}>
          {processedLabel} processed
        </Typography>
      )}
    </Paper>
  );
};

const AppTooltip = ({ title, children }) => (
  <Tooltip
    title={<Typography sx={{ fontFamily: FONT_REGULAR, fontSize: "13px" }}>{title}</Typography>}
    arrow
    enterDelay={250}
    leaveDelay={100}
    {...tooltipProps}
  >
    {children}
  </Tooltip>
);

const getMainHeaderCellSx = (column) => {
  const baseSx = {
    whiteSpace: "nowrap",
    width: column.width,
    minWidth: column.width,
    maxWidth: column.width,
    backgroundColor: "#eef6ff",
  };

  if (column.key === "serial") {
    return {
      ...baseSx,
      position: "sticky",
      left: 0,
      zIndex: 8,
      boxShadow: "2px 0 0 #bfdbfe",
    };
  }

  if (column.key === "serviceMatchId") {
    return {
      ...baseSx,
      position: "sticky",
      left: listColumns.find((item) => item.key === "serial")?.width || 60,
      zIndex: 8,
      boxShadow: "2px 0 0 #bfdbfe",
    };
  }

  return baseSx;
};

const getMainStickyBodyCellSx = (key, rowIndex) => {
  const backgroundColor = rowIndex % 2 === 0 ? "#ffffff" : "#fbfdff";
  const baseSx = {
    position: "sticky",
    backgroundColor,
    zIndex: 5,
    boxShadow: "2px 0 0 #dbeafe",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  };

  if (key === "serial") return { ...baseSx, left: 0 };
  if (key === "serviceMatchId") return { ...baseSx, left: listColumns.find((item) => item.key === "serial")?.width || 60 };
  return {};
};

const getDetailHeaderCellSx = (column) => {
  const baseSx = {
    whiteSpace: "normal",
    width: column.width,
    minWidth: column.width,
    maxWidth: column.width,
    backgroundColor: "#eef6ff",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
  };

  if (column.key === "serial" || column.key === "consultantName") {
    return {
      ...baseSx,
      position: "sticky",
      left: detailStickyLeft[column.key],
      zIndex: 12,
      backgroundColor: "#eef6ff",
      borderRight: "1px solid #bfdbfe",
      boxShadow: "3px 0 0 rgba(191, 219, 254, 0.85)",
    };
  }

  if (column.key === "overallStatus" || column.key === "remarks") {
    return {
      ...baseSx,
      position: "sticky",
      right: detailStickyRight[column.key],
      zIndex: 13,
      backgroundColor: "#eef6ff",
      borderLeft: "1px solid #bfdbfe",
      boxShadow: "-3px 0 0 rgba(191, 219, 254, 0.85)",
    };
  }

  return baseSx;
};

const getDetailBodyCellSx = (column, rowIndex) => {
  const rowBackground = rowIndex % 2 === 0 ? "#ffffff" : "#fbfdff";
  const baseSx = {
    width: column.width,
    minWidth: column.width,
    maxWidth: column.width,
    whiteSpace: "normal",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
  };

  if (column.key === "serial" || column.key === "consultantName") {
    return {
      ...baseSx,
      position: "sticky",
      left: detailStickyLeft[column.key],
      zIndex: 7,
      backgroundColor: "#f8fbff !important",
      borderRight: "1px solid #dbeafe",
      boxShadow: "3px 0 0 rgba(219, 234, 254, 0.9)",
      fontFamily: column.key === "consultantName" ? `${FONT_BOLD} !important` : FONT_REGULAR,
      color: `${TABLE_TEXT_COLOR} !important`,
    };
  }

  if (column.key === "overallStatus" || column.key === "remarks") {
    return {
      ...baseSx,
      position: "sticky",
      right: detailStickyRight[column.key],
      zIndex: 8,
      backgroundColor: "#f8fbff !important",
      borderLeft: "1px solid #dbeafe",
      boxShadow: "-3px 0 0 rgba(219, 234, 254, 0.9)",
    };
  }

  return { ...baseSx, backgroundColor: rowBackground };
};

const SnapshotTable = ({ columns, rows, getValue }) => (
  <Table size="small" sx={{ tableLayout: "fixed", width: "100%", border: "1px solid #dbeafe" }}>
    <TableHead>
      <TableRow sx={tableHeadRowSx}>
        {columns.map((column) => (
          <TableCell
            key={column.key}
            align={column.align || "left"}
            sx={{
              width: column.width,
              minWidth: column.width,
              backgroundColor: "#eef6ff",
              whiteSpace: "normal",
              wordBreak: "break-word",
            }}
          >
            {column.label}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
    <TableBody>
      {rows.map((row, index) => (
        <TableRow key={row.id || index} sx={tableBodyRowSx}>
          {columns.map((column) => (
            <TableCell
              key={column.key}
              align={column.align || "left"}
              sx={{
                whiteSpace: "pre-line",
                wordBreak: "break-word",
                verticalAlign: "top",
              }}
            >
              {getValue(row, column.key, index + 1)}
            </TableCell>
          ))}
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

const ServiceMatchingPdfSnapshot = React.forwardRef(({ details }, ref) => {
  if (!details) return null;
  const record = { ...toServiceMatchingListRecord(details), details };
  const summaryRows = getSummaryExportRows(details);

  return (
    <Box
      ref={ref}
      data-service-matching-export-root="true"
      sx={{
        position: "fixed",
        left: "-10000px",
        top: 0,
        width: PDF_EXPORT_WIDTH,
        backgroundColor: "#ffffff",
        p: 3,
        fontFamily: FONT_REGULAR,
        color: "#334155",
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
        <Box>
          <Typography sx={{ fontFamily: FONT_BOLD, fontSize: "22px", color: "#0f172a" }}>
            Invoice Automation - Services Payment Details
          </Typography>
          <Typography sx={{ ...mutedTextSx, mt: 0.5 }}>
            {details.serviceMatchId} | {details.vendor} | {details.servicePeriod}
          </Typography>
        </Box>
        <StatusChip status={details.summary?.overallStatus || details.overallStatus} />
      </Stack>

      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        {summaryRows.map((row) => (
          <Grid item xs={3} key={row.Field}>
            <SummaryCard label={row.Field} value={row.Value} />
          </Grid>
        ))}
      </Grid>

      <Typography sx={{ mb: 1, fontFamily: FONT_BOLD, fontSize: MIN_FONT_SIZE, color: "#17324d" }}>
        Invoice Automation - Services Payment
      </Typography>
      <SnapshotTable columns={listColumns} rows={[record]} getValue={getMainExportCellValue} />

      <Typography sx={{ mt: 2, mb: 1, fontFamily: FONT_BOLD, fontSize: MIN_FONT_SIZE, color: "#17324d" }}>
        Line Items
      </Typography>
      <SnapshotTable columns={detailColumns} rows={details.lineItems || []} getValue={getDetailExportCellValue} />
    </Box>
  );
});

ServiceMatchingPdfSnapshot.displayName = "ServiceMatchingPdfSnapshot";

const ServiceMatching = () => {
  const [mode, setMode] = useState("main");
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState("");
  const [paginationData, setPaginationData] = useState({
    currentPage: 1,
    pageCount: 1,
    totalCount: 0,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [pageNumber, setPageNumber] = useState("1");
  const [sortConfig, setSortConfig] = useState({ key: "createdDate", direction: "desc" });
  const [selectedDetails, setSelectedDetails] = useState(null);
  const [detailSource, setDetailSource] = useState("saved");
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [comparing, setComparing] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");
  const [compareProgress, setCompareProgress] = useState(null);
  const [saveSuccessOpen, setSaveSuccessOpen] = useState(false);
  const [listRefreshKey, setListRefreshKey] = useState(0);
  const [pdfExportDetails, setPdfExportDetails] = useState(null);
  const [pdfExporting, setPdfExporting] = useState(false);
  const pdfExportRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setListError("");
    getServiceMatchingList({
      search: searchTerm,
      page: parseInt(pageNumber, 10) || 1,
      sortKey: sortConfig.key,
      sortOrder: sortConfig.direction,
    })
      .then((data) => {
        if (!mounted) return;
        setRecords(data.records || []);
        setPaginationData(data.pagination || {
          currentPage: 1,
          pageCount: 1,
          totalCount: 0,
        });
      })
      .catch((error) => {
        if (!mounted) return;
        setRecords([]);
        setPaginationData({ currentPage: 1, pageCount: 1, totalCount: 0 });
        setListError(error?.message || "Unable to fetch service payment results.");
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [searchTerm, pageNumber, sortConfig.key, sortConfig.direction, listRefreshKey]);

  useEffect(() => {
    if (!pdfExporting || !pdfExportDetails) return;
    let mounted = true;

    saveElementAsPdf(
      pdfExportRef.current,
      `${pdfExportDetails.serviceMatchId || "service_matching"}-snapshot.pdf`,
    ).finally(() => {
      if (!mounted) return;
      setPdfExporting(false);
      setPdfExportDetails(null);
    });

    return () => {
      mounted = false;
    };
  }, [pdfExporting, pdfExportDetails]);

  const handleSort = (key) => {
    setPageNumber("1");
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "desc" ? "asc" : "desc",
    }));
  };

  const renderSortLabel = (key, label) => (
    <TableSortLabel
      active={sortConfig.key === key}
      direction={sortConfig.key === key ? sortConfig.direction : "asc"}
      onClick={() => handleSort(key)}
      IconComponent={UnfoldMoreIcon}
      sx={{ "& .MuiTableSortLabel-icon": { opacity: 1, color: "inherit !important" } }}
    >
      {label}
    </TableSortLabel>
  );

  const filteredRecords = useMemo(
    () => getFilteredAndSortedRecords(records, searchTerm, sortConfig),
    [records, searchTerm, sortConfig],
  );

  const pageCount = Math.max(1, Number(paginationData.pageCount) || 1);
  const currentPage = Math.min(parseInt(pageNumber, 10) || 1, pageCount);
  const pagedRecords = getPagedRecords(filteredRecords, 1, PAGE_SIZE);

  const openDetails = async (record) => {
    setDetailsLoading(true);
    setMode("detail");
    setDetailSource("saved");
    const details = record.details || (await getServiceMatchingDetails(record));
    setSelectedDetails(details);
    setDetailsLoading(false);
  };

  const clearUploadState = () => {
    setSelectedFiles([]);
    setErrorMessage("");
    setSaveMessage("");
    setCompareProgress(null);
  };

  const clearExtractedResult = () => {
    setSelectedDetails(null);
    setDetailSource("extracted");
    clearUploadState();
    setMode("create");
  };

  const handleCompare = async () => {
    setComparing(true);
    setErrorMessage("");
    setSaveMessage("");
    setCompareProgress(null);
    try {
      const details = await compareServiceDocuments({
        files: selectedFiles,
        onProgress: setCompareProgress,
      });
      setSelectedDetails(details);
      setDetailSource("extracted");
      setRecords((currentRecords) => [
        { ...toServiceMatchingListRecord(details), details },
        ...currentRecords,
      ]);
      setMode("detail");
    } catch (error) {
      setErrorMessage(error?.message || "Unable to compare service documents.");
    } finally {
      setComparing(false);
      setCompareProgress(null);
    }
  };

  const handleSaveResult = async () => {
    setSaving(true);
    setSaveMessage("");
    setErrorMessage("");
    try {
      await saveServiceMatchingResult(selectedDetails?.batchToken);
      setSaveMessage("");
      setSaveSuccessOpen(true);
    } catch (error) {
      setErrorMessage(error?.message || "Unable to save service payment result.");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSuccessClose = () => {
    setSaveSuccessOpen(false);
    setSelectedDetails(null);
    setDetailSource("saved");
    setPageNumber("1");
    setListRefreshKey((value) => value + 1);
    setMode("main");
  };

  const exportRecordPdf = async (record) => {
    const details = record.details || (await getServiceMatchingDetails(record));
    if (!details) return;
    setPdfExportDetails(details);
    setPdfExporting(true);
  };

  const exportSelectedDetails = () => {
    if (!selectedDetails) return;
    const record = { ...toServiceMatchingListRecord(selectedDetails), details: selectedDetails };
    exportRecordPdf(record);
  };

  const renderMain = () => (
    <>
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} p={2} gap={2}>
        <HeaderTitle titleText="Invoice Automation - Services Payment" />
        <AppTooltip title="Create Invoice Automation - Services Payment record by uploading SOW, timesheet approval and invoice files for 2-way or 3-way matching">
          <Button
            variant="contained"
            color="primary"
            startIcon={<CloudUploadIcon />}
            onClick={() => setMode("create")}
            sx={{ ...actionButtonSx, minHeight: "40px" }}
          >
            Create Service Matching
          </Button>
        </AppTooltip>
      </Stack>

      <Box sx={{ p: 2, pt: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2, mb: 2 }}>
          <Box sx={{ flex: 1, minWidth: 260, maxWidth: 460 }}>
            <SearchBar
              value={searchTerm}
              onChange={(value) => {
                setSearchTerm(value);
                setPageNumber("1");
              }}
              placeholder="Search by Service Match ID, SOW No, vendor, period, timesheet..."
            />
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ px: 2, py: 4 }}><LinearProgress /></Box>
        ) : listError ? (
          <EmptyState title="Unable to load records" subtitle={listError} />
        ) : filteredRecords.length === 0 ? (
          <EmptyState title="No records found" subtitle="Try clearing the search, or create your first Invoice Automation - Service Payment record." />
        ) : (
          <Paper elevation={2} sx={tablePaperSx}>
            <TableContainer sx={{ ...tableContainerSx, maxHeight: "60vh" }}>
              <Table size="small" stickyHeader sx={{ minWidth: listTableMinWidth, tableLayout: "fixed", borderRight: "1px solid #e2e8f0" }}>
                <TableHead>
                  <TableRow sx={tableHeadRowSx}>
                    {listColumns.map((column) => (
                      <TableCell key={column.key} align={column.align || "left"} sx={getMainHeaderCellSx(column)}>
                        {column.sortable ? renderSortLabel(column.key, column.label) : column.label}
                      </TableCell>
                    ))}
                    <TableCell align="center" sx={{ position: "sticky", right: 0, width: 90, minWidth: 90, zIndex: 9, backgroundColor: "#eef6ff", borderLeft: "1px solid #bfdbfe", boxShadow: "-3px 0 0 #dbeafe" }}>
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedRecords.map((record, index) => (
                    <TableRow key={record.id} hover sx={tableBodyRowSx}>
                      <TableCell sx={getMainStickyBodyCellSx("serial", index)}>{(currentPage - 1) * PAGE_SIZE + index + 1}</TableCell>
                      <TableCell sx={{ ...getMainStickyBodyCellSx("serviceMatchId", index), fontFamily: `${FONT_BOLD} !important`, color: `${TABLE_TEXT_COLOR} !important` }}>{record.serviceMatchId}</TableCell>
                      <TableCell>{record.matchingMode}</TableCell>
                      <TableCell>{record.sowNo}</TableCell>
                      <TableCell sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        <AppTooltip title={record.vendor || "-"}>
                          <span>{record.vendor || "-"}</span>
                        </AppTooltip>
                      </TableCell>
                      <TableCell>{record.servicePeriod}</TableCell>
                      <TableCell>{record.invoiceNumber || "-"}</TableCell>
                      <TableCell align="right">{record.resources}</TableCell>
                      <TableCell align="right">{formatNumber(record.approvedHours, 0)}</TableCell>
                      <TableCell align="right">
                        <PriceValue value={record.expectedBilling} currency={record.currency} />
                      </TableCell>
                      <TableCell align="right">
                        <PriceValue value={record.invoiceAmount} currency={record.currency} />
                      </TableCell>
                      <TableCell align="right">
                        <PriceValue value={record.varianceAmount} currency={record.currency} />
                      </TableCell>
                      <TableCell>{record.currency}</TableCell>
                      {/* <TableCell align="right">
                        {formatInrAmount(record.inrEquivalent)}
                        <Typography sx={mutedTextSx}>
                          {record.exchangeRate?.sourceCurrency}/{record.exchangeRate?.targetCurrency} {formatNumber(record.exchangeRate?.exchangeRate, 4)}
                        </Typography>
                      </TableCell> */}
                      <TableCell><StatusChip status={record.overallStatus} /></TableCell>
                      <TableCell>{formatDate(record.createdDate)}</TableCell>
                      <TableCell align="center" sx={{ position: "sticky", right: 0, width: 90, minWidth: 90, backgroundColor: index % 2 === 0 ? "#ffffff" : "#fbfdff", borderLeft: "1px solid #dbeafe", boxShadow: "-3px 0 0 #dbeafe", zIndex: 6 }}>
                        <AppTooltip title="Export complete record snapshot as PDF">
                          <IconButton size="small" onClick={() => exportRecordPdf(record)} disabled={pdfExporting}>
                            <DownloadIcon sx={{ fontSize: 18, color: "#0f4c81" }} />
                          </IconButton>
                        </AppTooltip>
                        <AppTooltip title="View">
                          <IconButton size="small" onClick={() => openDetails(record)}>
                            <VisibilityIcon sx={{ fontSize: 18, color: "#1976d2" }} />
                          </IconButton>
                        </AppTooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ borderTop: "1px solid #e2e8f0", px: 2, py: 1.5 }}>
              <PaginationComponent
                pageNumber={String(currentPage)}
                currentPage={currentPage}
                itemsPerPage={paginationData.totalCount || filteredRecords.length}
                totalItems={pageCount}
                localStorageKey="service_matching_page_number"
                previouPageClick={() => setPageNumber(String(Math.max(1, currentPage - 1)))}
                nextPageClick={() => setPageNumber(String(Math.min(pageCount, currentPage + 1)))}
                setPageNumber={setPageNumber}
                disablePrevious={currentPage === 1}
                disableNext={currentPage === pageCount}
              />
            </Box>
          </Paper>
        )}
      </Box>
    </>
  );

  const renderCreate = () => (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <HeaderTitle titleText="Create Invoice Automation - Service Payment" />
        <AppTooltip title="Back to Service Payment list">
          <Button variant="contained" startIcon={<ArrowBackIcon />} onClick={() => setMode("main")} sx={actionButtonSx}>
            Back
          </Button>
        </AppTooltip>
      </Stack>
      <Paper elevation={2} sx={{ p: { xs: 2, md: 3 }, borderRadius: 2, boxShadow: "0 2px 12px rgba(0, 0, 0, 0.08)" }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <MultiFileUploadBox files={selectedFiles} onChange={setSelectedFiles} onClear={clearUploadState} />
          </Grid>
        </Grid>
        {errorMessage && (
          <Typography sx={{ ...mutedTextSx, mt: 2, color: "#d32f2f" }}>
            {errorMessage}
          </Typography>
        )}
        <ProcessStatusPanel progress={compareProgress} />
        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="flex-end" mt={3} gap={1.5}>
          <AppTooltip title="Cancel and return to Service payment list">
            <Button variant="outlined" onClick={() => setMode("main")} sx={actionButtonSx}>
              Cancel
            </Button>
          </AppTooltip>
          <AppTooltip title="Upload selected files and extract matching result">
            <span>
              <Button
                variant="contained"
                disabled={selectedFiles.length === 0 || comparing}
                onClick={handleCompare}
                startIcon={comparing ? <CircularProgress size={16} color="inherit" /> : <CloudUploadIcon />}
                sx={actionButtonSx}
              >
                {comparing ? "Comparing Documents..." : "Compare Documents"}
              </Button>
            </span>
          </AppTooltip>
        </Stack>
      </Paper>
    </Box>
  );

  const renderDetail = () => {
    const details = selectedDetails;
    const exchange = details?.exchangeRate || {};

    return (
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} mb={2} gap={2}>
          <Box>
            <HeaderTitle titleText="Invoice Automation - Service Payment Details" />
            {details && (
              <Typography sx={{ ...mutedTextSx, mt: 0.5 }}>
                {details.serviceMatchId} | {details.vendor} | {details.servicePeriod}
              </Typography>
            )}
          </Box>
          <Stack direction="row" gap={1} flexWrap="wrap">
            <AppTooltip title="Export current Service payment result snapshot as PDF">
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={exportSelectedDetails}
                disabled={!details || pdfExporting}
                sx={actionButtonSx}
              >
                {pdfExporting ? "Exporting..." : "Export PDF"}
              </Button>
            </AppTooltip>
            {detailSource === "extracted" && (
              <AppTooltip title="Clear extracted result and upload another document set">
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<CancelIcon />}
                  onClick={clearExtractedResult}
                  sx={actionButtonSx}
                >
                  Clear Result
                </Button>
              </AppTooltip>
            )}
            <AppTooltip title="Save service payment result">
              <span>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                  onClick={handleSaveResult}
                  disabled={!details?.batchToken || saving}
                  sx={actionButtonSx}
                >
                  {saving ? "Saving..." : "Save"}
                </Button>
              </span>
            </AppTooltip>
            <AppTooltip title="Back to Service Payment list">
              <Button variant="contained" startIcon={<ArrowBackIcon />} onClick={() => setMode("main")} sx={actionButtonSx}>
                Back
              </Button>
            </AppTooltip>
          </Stack>
        </Stack>

        {detailsLoading || !details ? (
          <Box sx={{ px: 2, py: 4 }}><LinearProgress /></Box>
        ) : (
          <>
            {(saveMessage || errorMessage) && (
              <Typography
                sx={{
                  ...mutedTextSx,
                  mb: 2,
                  color: saveMessage ? "#2e7d32" : "#d32f2f",
                }}
              >
                {saveMessage || errorMessage}
              </Typography>
            )}
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6} md={3}><SummaryCard label="Overall Status" value={<StatusChip status={details.summary.overallStatus} />} /></Grid>
              <Grid item xs={12} sm={6} md={3}><SummaryCard label="Matching Mode" value={details.matchingMode} /></Grid>
              <Grid item xs={12} sm={6} md={3}><SummaryCard label="Matched / Unmatched" value={`${formatNumber(details.summary.matchedCount, 0)} / ${formatNumber(details.summary.unmatchedCount, 0)}`} /></Grid>
              <Grid item xs={12} sm={6} md={3}><SummaryCard label="Contract Coverage" value={details.summary.contractCoverage} /></Grid>
              <Grid item xs={12} sm={6} md={3}><SummaryCard label="Timesheet Approval" value={details.summary.timesheetApproval} /></Grid>
              <Grid item xs={12} sm={6} md={3}><SummaryCard label="Special Billing Conditions" value={details.summary.specialBillingConditions} /></Grid>
              <Grid item xs={12} sm={6} md={3}><SummaryCard label="Period Issues" value={details.summary.periodIssues} /></Grid>
              <Grid item xs={12} sm={6} md={3}><SummaryCard label="Original Expected Billing" value={formatAmount(details.summary.originalExpectedBilling, details.currency)} /></Grid>
              <Grid item xs={12} sm={6} md={3}><SummaryCard label="Invoice Amount" value={formatAmount(details.summary.invoiceAmount, details.currency)} /></Grid>
              <Grid item xs={12} sm={6} md={3}><SummaryCard label="Invoice Variance" value={formatAmount(details.summary.invoiceVariance, details.currency)} /></Grid>
              <Grid item xs={12} sm={6} md={3}><SummaryCard label="Exchange Rate + Date" value={`${exchange.sourceCurrency}/${exchange.targetCurrency} ${formatNumber(exchange.exchangeRate, 4)} | ${formatDate(exchange.exchangeRateDate)}`} /></Grid>
              <Grid item xs={12} sm={6} md={3}><SummaryCard label="Expected Billing in INR" value={formatInrAmount(details.summary.expectedBillingInInr)} /></Grid>
            </Grid>

            <Paper elevation={2} sx={tablePaperSx}>
              <TableContainer sx={{ ...tableContainerSx, maxHeight: "62vh" }}>
                <Table size="small" stickyHeader sx={{ minWidth: detailTableMinWidth, tableLayout: "fixed", borderRight: "1px solid #e2e8f0" }}>
                  <TableHead>
                    <TableRow sx={tableHeadRowSx}>
                      {detailColumns.map((column) => (
                        <TableCell key={column.key} align={column.align || "left"} sx={getDetailHeaderCellSx(column)}>
                          {detailColumnTooltips[column.key] ? (
                            <AppTooltip title={detailColumnTooltips[column.key]}>
                              <span>{column.label}</span>
                            </AppTooltip>
                          ) : (
                            column.label
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {details.lineItems.map((row, index) => (
                      <TableRow key={row.id} hover sx={tableBodyRowSx}>
                        <TableCell sx={getDetailBodyCellSx(getDetailColumn("serial"), index)}>{index + 1}</TableCell>
                        <TableCell sx={getDetailBodyCellSx(getDetailColumn("consultantName"), index)}>{row.consultantName}</TableCell>
                        <TableCell sx={getDetailBodyCellSx(getDetailColumn("role"), index)}>{row.role}</TableCell>
                        <TableCell sx={getDetailBodyCellSx(getDetailColumn("currency"), index)}>{row.currency}</TableCell>
                        <TableCell align="right" sx={getDetailBodyCellSx(getDetailColumn("hourlyRate"), index)}>
                          <PriceValue value={row.hourlyRate} currency={row.currency} />
                        </TableCell>
                        <TableCell align="right" sx={getDetailBodyCellSx(getDetailColumn("fixedFee"), index)}>
                          <PriceValue value={row.fixedFee} currency={row.currency} />
                        </TableCell>
                        <TableCell align="right" sx={getDetailBodyCellSx(getDetailColumn("approvedHours"), index)}>{formatNumber(row.approvedHours, 0)}</TableCell>
                        <TableCell align="right" sx={getDetailBodyCellSx(getDetailColumn("billingPercentage"), index)}>{formatNumber(row.billingPercentage, 0)}%</TableCell>
                        <TableCell align="right" sx={getDetailBodyCellSx(getDetailColumn("billableHours"), index)}>{formatNumber(row.billableHours, 2)}</TableCell>
                        <TableCell align="right" sx={getDetailBodyCellSx(getDetailColumn("expectedAmount"), index)}>
                          <PriceValue value={row.expectedAmount} currency={row.currency} />
                        </TableCell>
                        <TableCell align="right" sx={getDetailBodyCellSx(getDetailColumn("exchangeRate"), index)}>
                          {formatNumber(row.exchangeRate, 4)}
                          <Typography sx={mutedTextSx}>
                            {formatDate(row.exchangeRateDate)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={getDetailBodyCellSx(getDetailColumn("invoiceHours"), index)}>{formatNumber(row.invoiceHours, 2)}</TableCell>
                        <TableCell align="right" sx={getDetailBodyCellSx(getDetailColumn("invoiceHourlyRate"), index)}><PriceValue value={row.invoiceHourlyRate} currency={row.currency} /></TableCell>
                        <TableCell align="right" sx={getDetailBodyCellSx(getDetailColumn("invoiceAmount"), index)}><PriceValue value={row.invoiceAmount} currency={row.currency} /></TableCell>
                        <TableCell align="right" sx={getDetailBodyCellSx(getDetailColumn("invoiceVariance"), index)}><PriceValue value={row.invoiceVariance} currency={row.currency} /></TableCell>
                        <TableCell sx={getDetailBodyCellSx(getDetailColumn("matchStatus"), index)}><StatusChip status={row.matchStatus} /></TableCell>
                        <TableCell sx={getDetailBodyCellSx(getDetailColumn("contractStatus"), index)}><StatusChip status={row.contractStatus} /></TableCell>
                        <TableCell sx={getDetailBodyCellSx(getDetailColumn("approvalStatus"), index)}><StatusChip status={row.approvalStatus} /></TableCell>
                        <TableCell sx={getDetailBodyCellSx(getDetailColumn("periodStatus"), index)}><StatusChip status={row.periodStatus} /></TableCell>
                        <TableCell sx={getDetailBodyCellSx(getDetailColumn("billingStatus"), index)}><StatusChip status={row.billingStatus} /></TableCell>
                        <TableCell sx={getDetailBodyCellSx(getDetailColumn("invoiceStatus"), index)}><StatusChip status={row.invoiceStatus} /></TableCell>
                        <TableCell sx={getDetailBodyCellSx(getDetailColumn("overallStatus"), index)}><StatusChip status={row.overallStatus} /></TableCell>
                        <TableCell sx={getDetailBodyCellSx(getDetailColumn("remarks"), index)}>{row.remarks}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </>
        )}
      </Box>
    );
  };

  return (
    <>
      {mode === "create" ? renderCreate() : mode === "detail" ? renderDetail() : renderMain()}
      <Modal open={saveSuccessOpen}>
        <Fade in={saveSuccessOpen}>
          <Box>
            <SuccessPopupDashboard
              popupImage={successPopupImg}
              popupHeroText="Invoice Automation - Service Payment Saved!"
              popupDescription="The Invoice Automation - Service Paymentresult has been saved successfully."
              popBtn
              vendorBtn
              btnText="Okay"
              btnClick={handleSaveSuccessClose}
              btnWidth="12rem"
              btnHeight="4rem"
            />
          </Box>
        </Fade>
      </Modal>
      <ServiceMatchingPdfSnapshot ref={pdfExportRef} details={pdfExportDetails} />
    </>
  );
};

export default ServiceMatching;
